import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, StatusBadge, PageLoader, Btn, TextInput, TextArea, brandName } from "@/lib/ui";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const COLUMNS = ["DRAFT", "AI PREPARED", "REVIEW REQUIRED", "APPROVED", "REJECTED", "SCHEDULED", "PUBLISHED", "ARCHIVED", "ROLLED BACK"];
const TONE = {
  "DRAFT": "muted", "AI PREPARED": "proc", "REVIEW REQUIRED": "warn", "APPROVED": "ok",
  "REJECTED": "err", "SCHEDULED": "info", "PUBLISHED": "ok", "ARCHIVED": "muted", "ROLLED BACK": "err",
};

function NewApproval({ brand }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", action: "", content: "", category: "general" });
  const create = useMutation({
    mutationFn: () => api.post("/approvals", { ...form, brand_id: brand }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["approvals"] }); setOpen(false); setForm({ title: "", action: "", content: "", category: "general" }); toast.success("Approval created"); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Btn data-testid="new-approval-btn"><Plus className="h-4 w-4" /> New Approval</Btn>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle className="font-head">New Approval Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <TextInput label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="approval-title" />
          <TextInput label="Action" placeholder="e.g. Publish launch announcement" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} data-testid="approval-action" />
          <TextInput label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <TextArea rows={3} label="Content / data affected" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        </div>
        <DialogFooter>
          <Btn onClick={() => create.mutate()} disabled={!form.title || !form.action || create.isPending} data-testid="approval-submit">Create</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalCard({ ap }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const trans = useMutation({
    mutationFn: (status) => api.post(`/approvals/${ap.id}/transition`, { status, note }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["approvals"] }); toast.success("Status updated"); setOpen(false); },
    onError: (e) => toast.error(e.message),
  });
  const nextActions = {
    "DRAFT": ["REVIEW REQUIRED"], "AI PREPARED": ["REVIEW REQUIRED"],
    "REVIEW REQUIRED": ["APPROVED", "REJECTED"], "APPROVED": ["SCHEDULED", "PUBLISHED", "ROLLED BACK"],
    "SCHEDULED": ["PUBLISHED", "ROLLED BACK"], "PUBLISHED": ["ARCHIVED", "ROLLED BACK"],
    "REJECTED": ["ARCHIVED"], "ROLLED BACK": ["ARCHIVED"], "ARCHIVED": [],
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`approval-card-${ap.id}`} className="text-left w-full bg-surface border border-border rounded-md p-3 hover:bg-accent/40 transition-colors">
          <div className="text-sm font-medium leading-tight">{ap.title}</div>
          <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{ap.action}</div>
          <div className="flex items-center gap-2 mt-2 text-[10px] font-mono text-muted-foreground">
            <span>{brandName(ap.brand_id)}</span>{ap.agent_code && <span>· {ap.agent_code}</span>}
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">{ap.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <StatusBadge tone={TONE[ap.status]}>{ap.status}</StatusBadge>
            <span className="text-xs font-mono text-muted-foreground">{ap.requesting_system}</span>
          </div>
          <div><span className="text-muted-foreground text-xs">Action:</span> {ap.action}</div>
          {ap.content && <div className="text-foreground/80 border border-border rounded-md p-2 text-xs whitespace-pre-wrap">{ap.content}</div>}
          <div className="text-xs text-muted-foreground">Reviewer: {ap.reviewer || "—"}</div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Version History</div>
            <div className="space-y-1">
              {(ap.history || []).map((h, i) => (
                <div key={i} className="text-[11px] font-mono flex items-center gap-2">
                  <StatusBadge tone={TONE[h.status]}>{h.status}</StatusBadge>
                  <span className="text-muted-foreground">{h.by}</span>
                </div>
              ))}
            </div>
          </div>
          <TextInput placeholder="Reviewer note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-note" />
          <div className="flex flex-wrap gap-2">
            {(nextActions[ap.status] || []).map((st) => (
              <Btn key={st} size="sm" variant={st === "REJECTED" || st === "ROLLED BACK" ? "danger" : "outline"}
                onClick={() => trans.mutate(st)} disabled={trans.isPending} data-testid={`transition-${st.replace(/\s+/g, "-")}`}>
                {st}
              </Btn>
            ))}
            {(nextActions[ap.status] || []).length === 0 && <span className="text-xs text-muted-foreground">No further transitions.</span>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Approvals() {
  const api = useApi();
  const { brand } = useBrand();
  const { data: approvals = [], isLoading } = useQuery({ queryKey: ["approvals", brand], queryFn: () => api.get(`/approvals?brand_id=${brand}`) });

  if (isLoading) return <PageLoader />;
  const visible = COLUMNS.filter((c) => approvals.some((a) => a.status === c) || ["REVIEW REQUIRED", "APPROVED", "DRAFT"].includes(c));

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · Governance`} title="Approval Centre">
        <NewApproval brand={brand} />
      </PageHeader>

      <div className="flex gap-3 overflow-x-auto pb-4 snap-x">
        {visible.map((col) => {
          const items = approvals.filter((a) => a.status === col);
          return (
            <div key={col} data-testid={`approval-col-${col.replace(/\s+/g, "-")}`} className="w-72 shrink-0 snap-start">
              <div className="flex items-center justify-between mb-2 px-1">
                <StatusBadge tone={TONE[col]}>{col}</StatusBadge>
                <span className="text-xs font-mono text-muted-foreground">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px] bg-surface/30 border border-border rounded-md p-2">
                {items.length === 0 ? (
                  <div className="text-[11px] text-muted-foreground/60 text-center py-6">Empty</div>
                ) : items.map((ap) => <ApprovalCard key={ap.id} ap={ap} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
